
import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import SignInForm from './SignInForm';
import SignUpForm from './SignUpForm';
import ResetPasswordForm from './ResetPasswordForm';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const AuthForm: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const { signInWithGoogle } = useAuth();
  const { t } = useTranslation('auth');

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const { error } = await signInWithGoogle();
      if (error) toast.error(t('error_google'));
      else toast.success(t('redirecting_google'));
    } catch {
      toast.error(t('error_google'));
    } finally {
      setLoading(false);
    }
  };

  if (showResetPassword) {
    return (
      <div style={{ background: '#1C1C1C', borderRadius: '32px', border: '1px solid rgba(255,255,255,0.08)', overflow: 'hidden' }}>
        <div className="p-6">
          <ResetPasswordForm onBack={() => setShowResetPassword(false)} />
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: '#0D0D0D',
      borderRadius: '28px',
      border: '1px solid rgba(255,255,255,0.03)',
      overflow: 'hidden',
      boxShadow: '0 25px 60px rgba(0,0,0,0.9)'
    }}>

      {/* ── Illustration animaux en haut de la card ── */}
      <div className="relative w-full aspect-[16/10] overflow-hidden bg-[#0D0D0D]">
        <img
          src="/pets-illustration.webp"
          srcSet="/pets-illustration-400w.webp 400w, /pets-illustration-800w.webp 800w, /pets-illustration.webp 3584w"
          sizes="(max-width: 500px) 400px, (max-width: 900px) 800px, 3584px"
          alt="OctoVeto"
          draggable={false}
          fetchPriority="high"
          className="w-full h-full object-cover object-center select-none"
          style={{ mixBlendMode: 'normal' }}
        />
      </div>

      {/* Titre et sous-titre */}
      <div className="text-center px-6 pt-6 pb-2">
        <h1 className="text-2xl font-bold text-white tracking-tight">
          {t('welcome')}
        </h1>
        <p className="text-[13px] mt-1.5 leading-relaxed text-muted-foreground font-medium whitespace-pre-line">
          {t('subtitle')}
        </p>
      </div>

      {/* ── Contenu formulaire ── */}
      <div className="px-6 pb-8 pt-4 space-y-6">
        <Tabs defaultValue="signin" className="w-full">
          {/* Onglets pills sombres style Image 1 - Actif en Jaune Doré */}
          <TabsList
            className="w-full h-[48px] p-1 mb-6 bg-[#1A1A1A] border border-white/5 rounded-2xl"
          >
            <TabsTrigger
              value="signin"
              className="flex-1 rounded-xl text-sm font-bold transition-all duration-300 data-[state=active]:bg-[#C5A059] data-[state=active]:text-black data-[state=inactive]:text-muted-foreground/70"
            >
              {t('tab_signin')}
            </TabsTrigger>
            <TabsTrigger
              value="signup"
              className="flex-1 rounded-xl text-sm font-bold transition-all duration-300 data-[state=active]:bg-[#C5A059] data-[state=active]:text-black data-[state=inactive]:text-muted-foreground/70"
            >
              {t('tab_signup')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="signin" className="mt-0">
            <SignInForm
              onShowResetPassword={() => setShowResetPassword(true)}
              loading={loading}
              setLoading={setLoading}
            />
          </TabsContent>

          <TabsContent value="signup" className="mt-0">
            <SignUpForm loading={loading} setLoading={setLoading} />
          </TabsContent>
        </Tabs>

        {/* Séparateur minimaliste */}
        <div className="flex items-center gap-4 py-1">
          <div className="flex-1 h-[1px] bg-white/[0.03]" />
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/60">{t('or_separator')}</span>
          <div className="flex-1 h-[1px] bg-white/[0.03]" />
        </div>

        {/* Bouton Google Pill Blanche */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={loading}
          className="w-full flex items-center justify-center gap-3 rounded-full font-bold text-sm transition-all hover:brightness-95 active:scale-[0.98] disabled:opacity-50"
          style={{
            height: '52px',
            background: '#FFFFFF',
            color: '#000000',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {t('continue_google')}
        </button>
      </div>
    </div>
  );
};

export default AuthForm;
